import React, { useMemo } from 'react'

import { useMetricActions } from 'redux/actions/Metrics'

import { EAccountRole, TAccount } from 'types/GlobalAccount'
import { TGroup } from 'types/GlobalGroup'
import { ISelectMetric } from 'types/GlobalMetric'

import { useTranslation } from 'language/translator'
import { useAccounts } from 'redux/hooks/Accounts'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useGroups } from 'redux/hooks/Groups'
import { useMetric } from 'redux/hooks/Metrics'
import { useParams } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tooltip from 'components_new/atoms/Tooltip'

import FormSection from 'components_new/molecules/FormSection'
import FormToggle from 'components_new/molecules/FormToggle'
import GenericListItem from 'components_new/molecules/GenericListItem'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'

import {
  formatGroupOption,
  formatUsername,
  formatUserOption
} from 'utils/functions'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

interface MetricSettingsProps {
  edit: boolean
}

interface IPermissionBody {
  account_id?: string
  group_id?: string
}

const usePermissions = (
  metric: ISelectMetric | null
): {
  raw: IPermissionBody[]
  groups: TGroup[]
  accounts: TAccount[]
  options: (Option & { id: string })[]
} => {
  const { t } = useTranslation()
  const { account } = useAccountSettings()
  const { accounts, accountBody } = useAccounts()
  const { allGroups } = useGroups()

  const rawPermissions = useMemo<IPermissionBody[]>(() => {
    const fallback = metric?.access.permissions ?? []

    return fallback.map((item) => ({
      account_id: item.account_id ?? undefined,
      group_id: item.group_id ?? undefined
    }))
  }, [metric?.access.permissions])

  const groupPermissions = useMemo(() => {
    return rawPermissions
      .map((item) => {
        return item.group_id
          ? allGroups.find((g) => g.id === item.group_id)
          : null
      })
      .filter((item): item is TGroup => item !== null)
      .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
  }, [rawPermissions, allGroups])

  const accountPermissions = useMemo<TAccount[]>(() => {
    return rawPermissions
      .map((item) => {
        if (item.account_id && accountBody[item.account_id]) {
          return accountBody[item.account_id]
        }

        return null
      })
      .filter((a): a is TAccount => a !== null)
      .sort((a, b) => sortAlphabeticalAsc(a.first_name, b.first_name))
  }, [rawPermissions, accountBody])

  const accountOptions = useMemo(() => {
    return accounts
      .filter(
        (account) =>
          account.role === EAccountRole.VIEW &&
          account.id !== account?.id &&
          // exlude added users
          !rawPermissions.some((perm) => perm.account_id === account.id)
      )
      .map(formatUserOption)
  }, [accounts, account, rawPermissions])

  const groupOptions = useMemo(() => {
    return allGroups
      .filter(
        (group) =>
          // exlude added groups
          !rawPermissions.some((perm) => perm.group_id === group.id)
      )
      .map((group) => formatGroupOption(group, t))
  }, [allGroups, rawPermissions])

  return {
    raw: rawPermissions,
    groups: groupPermissions,
    accounts: accountPermissions,
    options: [...accountOptions, ...groupOptions]
  }
}

const PermissionSettings = (props: MetricSettingsProps) => {
  const { edit } = props
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()

  const { tryUpdateMetricSettings } = useMetricActions()

  const metric = useMetric(id, null)

  const permissions = usePermissions(metric)

  if (!metric) {
    return null
  }

  return (
    <FormSection
      helperText={t('metrics', 'access_description')}
      importance="optional"
      title={t('global', 'access')}
    >
      <FormToggle
        body={t('metrics', 'private_metric_description')}
        disabled={!edit}
        icon={{ name: 'Private' }}
        onChange={() =>
          tryUpdateMetricSettings(id, { private: !metric.access.private })
        }
        title={t('metrics', 'private_metric')}
        value={metric.access.private}
      />

      {edit && metric.access.private ? (
        <SelectSearch
          closeOnSelect={true}
          color="primary"
          disableClearable={true}
          label={t('metrics', 'add_user_or_group')}
          onChange={(option) => {
            const attributeKey =
              option.avatar === 'group' ? 'group_id' : 'account_id'

            tryUpdateMetricSettings(id, {
              permissions: [
                ...permissions.raw,
                {
                  [attributeKey]: option.id
                }
              ]
            })
          }}
          multiple={false}
          options={permissions.options}
          selected={null}
          size={'medium'}
          sort={true}
          sx={{ mb: 2 }}
        />
      ) : null}

      {permissions.groups.map((group) => {
        if (edit) {
          return (
            <GenericListItem
              actions={
                <Box>
                  <Tooltip title={t('global', 'delete')}>
                    <IconButton
                      onClick={() => {
                        tryUpdateMetricSettings(id, {
                          // remove group from permissions
                          permissions: permissions.raw.filter(
                            (perm) => perm.group_id !== group.id
                          )
                        })
                      }}
                      size="small"
                    >
                      <Icon
                        fontSize="small"
                        name="RemoveCircleOutlineOutlined"
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              body={`${group.members.length} ${t('groups', 'members', true)}`}
              color={group.profile_color}
              key={group.id}
              size="medium"
              title={group.name}
              variant="group"
            />
          )
        }

        return (
          <GenericListItem
            action="to"
            body={`${group.members.length} ${t('groups', 'members', true)}`}
            color={group.profile_color}
            key={group.id}
            size="medium"
            title={group.name}
            to={`/groups/${group.id}`}
            variant="group"
          />
        )
      })}
      {permissions.accounts.map((user) => {
        if (edit) {
          return (
            <GenericListItem
              actions={
                <Box>
                  <Tooltip title={t('global', 'delete')}>
                    <IconButton
                      onClick={() => {
                        tryUpdateMetricSettings(id, {
                          // remove account from permissions
                          permissions: permissions.raw.filter(
                            (perm) => perm.account_id !== user.id
                          )
                        })
                      }}
                      size="small"
                    >
                      <Icon
                        fontSize="small"
                        name="RemoveCircleOutlineOutlined"
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              body={user.email}
              key={user.id}
              size="medium"
              title={formatUsername(user)}
              variant="user"
            />
          )
        }

        return (
          <GenericListItem
            body={user.email}
            key={user.id}
            size="medium"
            title={formatUsername(user)}
            variant="user"
          />
        )
      })}
    </FormSection>
  )
}

export default PermissionSettings
